import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { getContext } from '@picsart/miniapps-sdk';
import type { DriveToolsSettingsProps, IStorageFullUpload } from '@picsart/cloud-storage/models';
import { DEFAULT_REMOTE_SETTINGS, REMOTE_SETTINGS_TYPES } from 'config/constants';

type RemoteSettingsContextParams = {
  [REMOTE_SETTINGS_TYPES.upload_storage_full_popup_config]: IStorageFullUpload[];
  [REMOTE_SETTINGS_TYPES.drive_tools_touchpoint_config]: DriveToolsSettingsProps | undefined;
  [REMOTE_SETTINGS_TYPES.project_duplication_premium]: boolean;
  [REMOTE_SETTINGS_TYPES.drive_search_enabled]: boolean;
  [REMOTE_SETTINGS_TYPES.commenting_enabled]: boolean;
  [REMOTE_SETTINGS_TYPES.show_hidden_files]: boolean;
  [REMOTE_SETTINGS_TYPES.hop_rollout_settings]: {
    is_enabled?: boolean;
  };
};

const RemoteSettingsContext = createContext<RemoteSettingsContextParams>(DEFAULT_REMOTE_SETTINGS);

export const useRemoteSettings = () => useContext(RemoteSettingsContext);

type RemoteSettingsProviderProps = {
  children: ReactNode;
};

const RemoteSettingsProvider = ({ children }: RemoteSettingsProviderProps) => {
  const [remoteSettings, setRemoteSettings] = useState(DEFAULT_REMOTE_SETTINGS);

  useEffect(() => {
    (async () => {
      Object.entries(REMOTE_SETTINGS_TYPES).map(async ([key, value]) => {
        try {
          const settings = await getContext().handlers.getRemoteSettings(value, key);
          if (typeof settings !== 'object' || (typeof settings === 'object' && Object.keys(settings).length > 0)) {
            setRemoteSettings(prevState => ({
              ...prevState,
              [REMOTE_SETTINGS_TYPES[key as keyof typeof REMOTE_SETTINGS_TYPES]]: settings,
            }));
          }
        } catch (err: any) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
    })();
  }, []);

  return <RemoteSettingsContext.Provider value={remoteSettings}>{children}</RemoteSettingsContext.Provider>;
};

export default RemoteSettingsProvider;
